import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Megaphone, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useAnnouncements, useMarkAnnouncementAsRead } from '@/hooks/useAnnouncements';
import { cn } from '@/lib/utils';

export const AnnouncementsList = () => {
  const { data: announcements, isLoading } = useAnnouncements();
  const markAsReadMutation = useMarkAnnouncementAsRead();
  const [readAnnouncements, setReadAnnouncements] = useState<Set<string>>(new Set());

  const handleMarkAsRead = (announcementId: string) => {
    setReadAnnouncements(prev => new Set([...prev, announcementId]));
    markAsReadMutation.mutate(announcementId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!announcements || announcements.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-32 text-center">
          <Megaphone className="w-8 h-8 text-muted-foreground mb-2" />
          <p className="text-muted-foreground">لا توجد إعلانات حالياً</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {announcements.map((announcement) => {
        const isRead = readAnnouncements.has(announcement.id);
        const isUrgent = announcement.priority === 'urgent';
        
        return (
          <Card 
            key={announcement.id} 
            className={cn(
              "transition-all duration-200",
              isRead ? "opacity-60" : "border-l-4",
              isUrgent && !isRead ? "border-l-red-500" : "border-l-blue-500"
            )}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Megaphone className={cn(
                      "w-5 h-5",
                      isUrgent ? "text-red-500" : "text-blue-500"
                    )} />
                    {announcement.title}
                    {isUrgent && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        عاجل
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4" />
                    {formatDistanceToNow(new Date(announcement.created_at), { 
                      addSuffix: true, 
                      locale: ar 
                    })}
                  </CardDescription>
                </div>
                
                {!isRead && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMarkAsRead(announcement.id)}
                    disabled={markAsReadMutation.isPending}
                    className="text-xs"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    تم القراءة
                  </Button>
                )}
              </div>
            </CardHeader>
            
            <CardContent>
              <ScrollArea className="max-h-32">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {announcement.message}
                </p>
              </ScrollArea>
              
              {announcement.expire_date && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    ينتهي في: {new Date(announcement.expire_date).toLocaleDateString('ar')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};